import { useMemo } from "react";

import {
  Flex,
  FlexProps,
  Menu,
  MenuProps,
  Table,
  TableThProps,
  UnstyledButton,
} from "@mantine/core";

import { IconArrowsSort, IconSortAscending, IconSortDescending, IconX } from "@tabler/icons-react";

import { SortableThDirection } from "@/constants/sortable-th";

import { TextWrapper } from "../TextWrapper";

interface SortableThProps extends TableThProps {
  children: React.ReactNode;
  sortKey: string;
  searchParams: URLSearchParams;
  onSort: (sortKey: string | null) => void;
  flexProps?: FlexProps;
  menuPosition?: MenuProps["position"];
}

const SortableTh = ({
  children,
  sortKey,
  searchParams,
  onSort,
  flexProps,
  menuPosition = "bottom-end",
  ...props
}: SortableThProps) => {
  const currentSortKey = searchParams.get("sort");
  const direction = useMemo(() => {
    if (currentSortKey === sortKey) {
      return SortableThDirection.Asc;
    }

    if (currentSortKey === `-${sortKey}`) {
      return SortableThDirection.Desc;
    }

    return SortableThDirection.None;
  }, [currentSortKey, sortKey]);

  const iconStyle = { color: "var(--mantine-color-gray-6)", minWidth: 16, maxWidth: 16 };
  return (
    <Table.Th {...props}>
      <Menu withArrow shadow="sm" position={menuPosition}>
        <Menu.Target>
          <UnstyledButton w="100%" aria-label={`Sort files by ${children}`}>
            <Flex gap="sm" align="center" {...flexProps}>
              <TextWrapper ta="right" variant="bold">
                {children}
              </TextWrapper>
              {direction === SortableThDirection.Asc && <IconSortAscending style={iconStyle} />}
              {direction === SortableThDirection.Desc && <IconSortDescending style={iconStyle} />}
              {direction === SortableThDirection.None && <IconArrowsSort style={iconStyle} />}
            </Flex>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown aria-label="File sort">
          {direction !== SortableThDirection.Asc && (
            <Menu.Item onClick={() => onSort(sortKey)}>
              <Flex gap="xs" align="center">
                <IconSortAscending size={16} />
                <TextWrapper>Sort ascending</TextWrapper>
              </Flex>
            </Menu.Item>
          )}
          {direction !== SortableThDirection.Desc && (
            <Menu.Item onClick={() => onSort(`-${sortKey}`)}>
              <Flex gap="xs" align="center">
                <IconSortDescending size={16} />
                <TextWrapper>Sort descending</TextWrapper>
              </Flex>
            </Menu.Item>
          )}
          {direction !== SortableThDirection.None && (
            <Menu.Item onClick={() => onSort(null)}>
              <Flex gap="xs" align="center">
                <IconX size={16} />
                <TextWrapper>Clear sorting</TextWrapper>
              </Flex>
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    </Table.Th>
  );
};

export default SortableTh;
